import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import api from '@/utils/api';
import { Filter, Bot, X, ChevronDown, ArrowUpDown, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';
import Breadcrumbs from '@/components/Breadcrumbs';
import WishlistButton from '@/components/WishlistButton';
import { useCompare } from '@/components/CompareBar';
import { getDisplayPrice } from '@/utils/pricing';
import { telLink } from '@/utils/phone';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';

export default function ProductListingPage() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { tenantPath, branding } = useTenant();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [fittingRecommendation, setFittingRecommendation] = useState(null);
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(searchParams.get('brand') || '');
  const [showBrandFilter, setShowBrandFilter] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState(searchParams.get('type') || '');
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [sortBy, setSortBy] = useState('featured');
  const [selectedLaunch, setSelectedLaunch] = useState('');
  const [selectedSpin, setSelectedSpin] = useState('');
  const [priceMin, setPriceMin] = useState('');
  const [priceMax, setPriceMax] = useState('');
  const [quickView, setQuickView] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 24;
  const { toggle: toggleCompare, has: hasCompare } = useCompare();
  
  // Get branding colors
  const primaryColor = branding?.primary_color || '#4A6741';
  const accentColor = branding?.accent_color || '#D4A55A';
  
  useEffect(() => {
    loadProducts();
    checkFittingRecommendation();
  }, [category]);
  
  // Update selected brand when URL changes
  useEffect(() => {
    const brandFromUrl = searchParams.get('brand') || '';
    setSelectedBrand(brandFromUrl);
    const typeFromUrl = searchParams.get('type') || '';
    setSelectedSubcategory(typeFromUrl);
  }, [searchParams]);
  
  const checkFittingRecommendation = () => {
    const saved = localStorage.getItem('fittingRecommendation');
    if (saved) {
      const rec = JSON.parse(saved);
      // Only show if recent (within last hour)
      const appliedAt = new Date(rec.applied_at);
      const hourAgo = new Date(Date.now() - 60 * 60 * 1000);
      if (appliedAt > hourAgo) {
        setFittingRecommendation(rec);
      } else {
        localStorage.removeItem('fittingRecommendation');
      }
    }
  };
  
  const dismissRecommendation = () => {
    localStorage.removeItem('fittingRecommendation');
    setFittingRecommendation(null);
  };
  
  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products?category=${category}`);
      setProducts(response.data);
      
      // Extract unique brands for filter
      const uniqueBrands = [...new Set(response.data.map(p => p.manufacturer).filter(Boolean))].sort();
      setBrands(uniqueBrands);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };
  
  // Handle brand selection
  const handleBrandFilter = (brand) => {
    setSelectedBrand(brand);
    const params = {};
    if (brand) params.brand = brand;
    if (selectedSubcategory) params.type = selectedSubcategory;
    setSearchParams(params);
    setShowBrandFilter(false);
  };
  
  // Handle subcategory/type selection
  const handleTypeFilter = (type) => {
    setSelectedSubcategory(type);
    const params = {};
    if (selectedBrand) params.brand = selectedBrand;
    if (type) params.type = type;
    setSearchParams(params);
    setShowTypeFilter(false);
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setSelectedBrand('');
    setSelectedSubcategory('');
    setSearchParams({});
  };
  
  // Get subcategory label
  const getSubcategoryLabel = (sub) => {
    const labels = { wood: 'Driver/Wood', iron: 'Iron', hybrid: 'Hybrid', wedge: 'Wedge', putter: 'Putter' };
    return labels[sub] || sub;
  };
  
  // Get unique subcategories
  const subcategories = [...new Set(products.map(p => p.subcategory).filter(Boolean))].sort();
  
  // Filter products by search term, brand, subcategory, launch, spin, price
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.manufacturer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBrand = !selectedBrand || product.manufacturer === selectedBrand;
    const matchesType = !selectedSubcategory || product.subcategory === selectedSubcategory;
    const matchesLaunch = !selectedLaunch || product.shaft_characteristics?.launch === selectedLaunch;
    const matchesSpin = !selectedSpin || product.shaft_characteristics?.spin === selectedSpin;
    const priceMinNum = priceMin ? parseFloat(priceMin) : null;
    const priceMaxNum = priceMax ? parseFloat(priceMax) : null;
    const matchesPrice = (priceMinNum === null || getDisplayPrice(product) >= priceMinNum) &&
                         (priceMaxNum === null || getDisplayPrice(product) <= priceMaxNum);
    return matchesSearch && matchesBrand && matchesType && matchesLaunch && matchesSpin && matchesPrice;
  });

  // Sort products
  const sortedProducts = useMemo(() => {
    const list = [...filteredProducts];
    switch (sortBy) {
      case 'price-asc': list.sort((a, b) => getDisplayPrice(a) - getDisplayPrice(b)); break;
      case 'price-desc': list.sort((a, b) => getDisplayPrice(b) - getDisplayPrice(a)); break;
      case 'name-asc': list.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'name-desc': list.sort((a, b) => b.name.localeCompare(a.name)); break;
      default: break; // featured = server order
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProducts, sortBy]);

  // Pagination — reset to page 1 whenever filters/sort change the result set
  const totalPages = Math.max(1, Math.ceil(sortedProducts.length / PAGE_SIZE));
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedBrand, selectedSubcategory, selectedLaunch, selectedSpin, priceMin, priceMax, sortBy, category, setCurrentPage]);

  // Clamp current page if filter shrinks the list below current page
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const paginatedProducts = sortedProducts.slice(pageStart, pageStart + PAGE_SIZE);

  const goToPage = (n) => {
    const next = Math.min(Math.max(1, n), totalPages);
    setCurrentPage(next);
    // Smooth scroll back up so user sees the new page from the top of the grid
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Launch / Spin facet values (shafts only)
  const launchValues = ['Low', 'Mid', 'High'];
  const spinValues = ['Low', 'Mid', 'High'];
  const hasAnyActiveFilter = !!(selectedBrand || selectedSubcategory || selectedLaunch || selectedSpin || priceMin || priceMax || searchTerm);
  
  const getCategoryTitle = () => {
    const titles = {
      'shafts': 'Custom Shafts',
      'drivers': 'Custom Drivers',
      'fairways': 'Custom Fairways',
      'hybrids': 'Custom Hybrids',
      'irons': 'Custom Irons',
      'wedges': 'Custom Wedges',
      'putters': 'Custom Putters',
      'grips': 'Grips',
      'pre-owned': 'Pre-Owned Equipment'
    };
    return titles[category] || 'Products';
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-6 py-24 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading products...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <Breadcrumbs items={[{ label: getCategoryTitle() }]} />
        {/* Fitting Recommendation Banner */}
        {fittingRecommendation && category === 'shafts' && (
          <div className="mb-8 bg-[#4A6741]/10 border border-[#4A6741]/30 rounded-xl p-6" data-testid="fitting-recommendation-banner">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-[#4A6741] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Bot className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-[#2C3E2D] text-lg mb-1">Your Fitting Recommendation</h3>
                  <p className="text-[#5A6B5C] text-sm mb-3">
                    Based on your Club Fitting Wizard session • 
                    <span className="font-medium text-[#4A6741] ml-1">
                      {fittingRecommendation.option_type === 'distance' ? '🚀 Max Distance' : 
                       fittingRecommendation.option_type === 'control' ? '🎯 Max Control' : '⚖️ Balanced'} profile
                    </span>
                  </p>
                  <div className="flex flex-wrap gap-2 text-sm">
                    {fittingRecommendation.flex && (
                      <span className="bg-white px-3 py-1 rounded-full border border-[#4A6741]/30 text-[#2C3E2D]">
                        <span className="font-medium">Flex:</span> {fittingRecommendation.flex} {fittingRecommendation.flex_code && `(${fittingRecommendation.flex_code})`}
                      </span>
                    )}
                    {fittingRecommendation.weight_min && fittingRecommendation.weight_max && (
                      <span className="bg-white px-3 py-1 rounded-full border border-[#4A6741]/30 text-[#2C3E2D]">
                        <span className="font-medium">Weight:</span> {fittingRecommendation.weight_min}-{fittingRecommendation.weight_max}g
                      </span>
                    )}
                    {fittingRecommendation.launch && (
                      <span className="bg-white px-3 py-1 rounded-full border border-[#4A6741]/30 text-[#2C3E2D]">
                        <span className="font-medium">Launch:</span> {fittingRecommendation.launch}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#5A6B5C] mt-3">Click on a shaft to auto-apply your recommended configuration</p>
                </div>
              </div>
              <button 
                onClick={dismissRecommendation}
                className="text-[#5A6B5C] hover:text-[#2C3E2D] transition-colors p-1"
                aria-label="Dismiss recommendation"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="mb-12">
          <h1 
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'Chivo, sans-serif' }}
            data-testid="category-title"
          >
            {getCategoryTitle()}
            {selectedBrand && (
              <span className="text-2xl font-normal text-gray-500 ml-3">
                — {selectedBrand}
              </span>
            )}
          </h1>
          <p className="text-gray-600 text-lg">{filteredProducts.length} products available</p>
        </div>
        
        {/* Search & Filter */}
        <div className="mb-8 flex flex-wrap items-center gap-4">
          <input
            type="text"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 min-w-[200px] bg-gray-50 border border-[#F3F4F6] text-gray-900 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334155]"
            data-testid="search-input"
          />
          
          {/* Brand Filter Dropdown */}
          {brands.length > 0 && (
            <div className="relative">
              <button 
                onClick={() => { setShowBrandFilter(!showBrandFilter); setShowTypeFilter(false); }}
                className="h-12 px-5 bg-white border border-[#E8E4DC] text-gray-900 rounded-lg hover:border-gray-400 transition-all flex items-center gap-2"
                data-testid="brand-filter-btn"
              >
                <Filter className="h-4 w-4" />
                <span>{selectedBrand || 'All Brands'}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showBrandFilter ? 'rotate-180' : ''}`} />
              </button>
              
              {showBrandFilter && (
                <div 
                  className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-[#E8E4DC] py-2 z-50"
                  data-testid="brand-dropdown"
                >
                  <button
                    onClick={() => handleBrandFilter('')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${!selectedBrand ? 'font-bold' : ''}`}
                    style={{ color: !selectedBrand ? primaryColor : 'inherit' }}
                  >
                    All Brands
                  </button>
                  {brands.map((brand) => (
                    <button
                      key={brand}
                      onClick={() => handleBrandFilter(brand)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${selectedBrand === brand ? 'font-bold' : ''}`}
                      style={{ color: selectedBrand === brand ? primaryColor : 'inherit' }}
                      data-testid={`filter-brand-${brand.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {brand}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Subcategory/Type Filter Dropdown */}
          {subcategories.length > 1 && (
            <div className="relative">
              <button 
                onClick={() => { setShowTypeFilter(!showTypeFilter); setShowBrandFilter(false); }}
                className="h-12 px-5 bg-white border border-[#E8E4DC] text-gray-900 rounded-lg hover:border-gray-400 transition-all flex items-center gap-2"
                data-testid="type-filter-btn"
              >
                <Filter className="h-4 w-4" />
                <span>{selectedSubcategory ? getSubcategoryLabel(selectedSubcategory) : 'All Types'}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showTypeFilter ? 'rotate-180' : ''}`} />
              </button>
              
              {showTypeFilter && (
                <div 
                  className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-[#E8E4DC] py-2 z-50"
                  data-testid="type-dropdown"
                >
                  <button
                    onClick={() => handleTypeFilter('')}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${!selectedSubcategory ? 'font-bold' : ''}`}
                    style={{ color: !selectedSubcategory ? primaryColor : 'inherit' }}
                  >
                    All Types
                  </button>
                  {subcategories.map((sub) => (
                    <button
                      key={sub}
                      onClick={() => handleTypeFilter(sub)}
                      className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 transition-colors ${selectedSubcategory === sub ? 'font-bold' : ''}`}
                      style={{ color: selectedSubcategory === sub ? primaryColor : 'inherit' }}
                      data-testid={`filter-type-${sub}`}
                    >
                      {getSubcategoryLabel(sub)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Sort dropdown */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="h-12 pl-10 pr-8 bg-white border border-[#E8E4DC] text-gray-900 rounded-lg appearance-none cursor-pointer hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-[#334155]"
              data-testid="sort-select"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="name-asc">Name: A–Z</option>
              <option value="name-desc">Name: Z–A</option>
            </select>
            <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Price range */}
          <div className="flex items-center gap-1" data-testid="price-range">
            <input
              type="number"
              placeholder="Min $"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              className="w-24 h-12 px-3 bg-white border border-[#E8E4DC] text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334155]"
              data-testid="price-min-input"
            />
            <span className="text-gray-400">–</span>
            <input
              type="number"
              placeholder="Max $"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              className="w-24 h-12 px-3 bg-white border border-[#E8E4DC] text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#334155]"
              data-testid="price-max-input"
            />
          </div>

          {/* Clear filters button */}
          {hasAnyActiveFilter && (
            <button
              onClick={() => {
                clearAllFilters();
                setSelectedLaunch('');
                setSelectedSpin('');
                setPriceMin('');
                setPriceMax('');
                setSearchTerm('');
              }}
              className="h-12 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all flex items-center gap-2"
              data-testid="clear-filters"
            >
              <X className="h-4 w-4" />
              <span>Clear Filters</span>
            </button>
          )}
        </div>

        {/* Shaft-only facet pills */}
        {category === 'shafts' && (
          <div className="mb-6 flex flex-wrap gap-2" data-testid="shaft-facets">
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 mr-2 self-center">Launch:</span>
            {launchValues.map((v) => (
              <button
                key={v}
                onClick={() => setSelectedLaunch(selectedLaunch === v ? '' : v)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selectedLaunch === v
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`facet-launch-${v.toLowerCase()}`}
              >
                {v}
              </button>
            ))}
            <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 mx-2 self-center">Spin:</span>
            {spinValues.map((v) => (
              <button
                key={v}
                onClick={() => setSelectedSpin(selectedSpin === v ? '' : v)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  selectedSpin === v
                    ? 'bg-amber-600 text-white border-amber-600'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`facet-spin-${v.toLowerCase()}`}
              >
                {v}
              </button>
            ))}
          </div>
        )}

        {/* Shop by Brand (shown when no brand selected and there are brands) */}
        {!selectedBrand && brands.length >= 4 && (
          <div className="mb-10" data-testid="shop-by-brand">
            <h2 className="text-xs font-bold uppercase tracking-widest text-[#334155] mb-3">Shop by Brand</h2>
            <div className="flex flex-wrap gap-2">
              {brands.slice(0, 16).map((b) => (
                <button
                  key={b}
                  onClick={() => handleBrandFilter(b)}
                  className="px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-gray-800 hover:border-[#334155] hover:bg-gray-50 transition-all"
                  data-testid={`brand-chip-${b.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {b}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Products Grid */}
        {sortedProducts.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-gray-600 text-lg">No products found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12" data-testid="products-grid">
            {paginatedProducts.map((product) => (
              <Link
                key={product.product_id}
                to={tenantPath(`/product/${product.product_id}`)}
                className="group transition-transform duration-200 hover:-translate-y-1"
                data-testid={`product-card-${product.product_id}`}
              >
                <div className="relative bg-gray-50 p-0 overflow-hidden rounded-lg border border-transparent group-hover:border-gray-200 group-hover:shadow-lg transition-all mb-4">
                  <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                    {product.image_url ? (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="text-gray-500 text-sm">No image</div>
                    )}
                  </div>
                  {product.stock <= 5 && product.stock > 0 && (
                    <div className="absolute top-4 right-4 bg-[#EF4444] text-white text-xs font-bold px-3 py-1 rounded-lg">
                      Only {product.stock} left
                    </div>
                  )}
                  {product.stock === 0 && (
                    <div className="absolute top-4 right-4 bg-[#78716C] text-white text-xs font-bold px-3 py-1 rounded-lg">
                      Out of Stock
                    </div>
                  )}

                  {/* Hover action buttons */}
                  <div className="absolute top-3 left-3 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <WishlistButton productId={product.product_id} className="p-2 shadow-md" size={16} />
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuickView(product); }}
                      className="p-2 rounded-full bg-white/90 text-gray-600 hover:text-[#334155] hover:bg-white shadow-md transition-all"
                      aria-label="Quick view"
                      data-testid={`quick-view-${product.product_id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.preventDefault(); e.stopPropagation();
                        const res = toggleCompare(product);
                        if (res === 'full') toast.error('Compare limit is 3 items');
                      }}
                      className={`p-2 rounded-full shadow-md transition-all text-xs font-bold ${
                        hasCompare(product.product_id)
                          ? 'bg-[#334155] text-white'
                          : 'bg-white/90 text-gray-600 hover:bg-white'
                      }`}
                      aria-label="Compare"
                      data-testid={`compare-toggle-${product.product_id}`}
                    >
                      VS
                    </button>
                  </div>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-xs text-[#334155] font-bold uppercase tracking-wider">{product.manufacturer}</p>
                    {product.subcategory && (
                      <span
                        className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border"
                        style={{
                          color: primaryColor,
                          borderColor: `${primaryColor}40`,
                          backgroundColor: `${primaryColor}08`
                        }}
                        data-testid={`subcategory-badge-${product.product_id}`}
                      >
                        {getSubcategoryLabel(product.subcategory)}
                      </span>
                    )}
                    {category === 'shafts' && product.shaft_characteristics?.launch && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100">
                        {product.shaft_characteristics.launch} Launch
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                  {product.is_call_for_pricing ? (
                    <div data-testid={`call-for-pricing-${product.product_id}`}>
                      <p className="text-lg font-bold text-amber-700">Call for Pricing</p>
                      {product.call_for_pricing_phone && (
                        <span
                          role="link"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); e.preventDefault(); window.location.href = telLink(product.call_for_pricing_phone); }}
                          className="text-xs font-semibold text-amber-800 underline cursor-pointer"
                        >
                          {product.call_for_pricing_phone}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-[#334155]">${getDisplayPrice(product).toFixed(2)}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination controls */}
        {totalPages > 1 && (
          <div className="mt-12" data-testid="pagination-controls">
            <p className="text-center text-sm text-gray-500 mb-4">
              Showing <span className="font-semibold text-gray-800">{pageStart + 1}</span>–
              <span className="font-semibold text-gray-800">{Math.min(pageStart + PAGE_SIZE, sortedProducts.length)}</span>
              {' '}of{' '}
              <span className="font-semibold text-gray-800">{sortedProducts.length}</span>
            </p>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => { e.preventDefault(); goToPage(currentPage - 1); }}
                    className={currentPage === 1 ? 'pointer-events-none opacity-40' : ''}
                    data-testid="pagination-prev"
                  />
                </PaginationItem>
                {(() => {
                  // Compact page list: first, current ± 1, last, with ellipses
                  const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
                  const sorted = [...pages].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
                  const items = [];
                  let prev = 0;
                  for (const p of sorted) {
                    if (p - prev > 1) {
                      items.push(
                        <PaginationItem key={`e-${p}`}>
                          <PaginationEllipsis />
                        </PaginationItem>
                      );
                    }
                    items.push(
                      <PaginationItem key={p}>
                        <PaginationLink
                          href="#"
                          isActive={p === currentPage}
                          onClick={(e) => { e.preventDefault(); goToPage(p); }}
                          data-testid={`pagination-page-${p}`}
                        >
                          {p}
                        </PaginationLink>
                      </PaginationItem>
                    );
                    prev = p;
                  }
                  return items;
                })()}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => { e.preventDefault(); goToPage(currentPage + 1); }}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-40' : ''}
                    data-testid="pagination-next"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </div>

      {/* Quick View modal */}
      {quickView && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => setQuickView(null)}
          data-testid="quick-view-modal"
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="grid md:grid-cols-2">
              <div className="bg-gray-50 aspect-square flex items-center justify-center p-6">
                {quickView.image_url && (
                  <img src={quickView.image_url} alt={quickView.name} className="max-w-full max-h-full object-contain" />
                )}
              </div>
              <div className="p-6 flex flex-col">
                <button
                  onClick={() => setQuickView(null)}
                  className="self-end text-gray-400 hover:text-gray-700"
                  aria-label="Close quick view"
                  data-testid="quick-view-close"
                >
                  <X className="w-5 h-5" />
                </button>
                <p className="text-xs font-bold uppercase tracking-widest text-[#334155] mb-1">
                  {quickView.manufacturer}
                </p>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{quickView.name}</h3>
                <p className="text-3xl font-bold text-[#334155] mb-4">
                  {quickView.is_call_for_pricing ? 'Call for Pricing' : `$${getDisplayPrice(quickView).toFixed(2)}`}
                </p>
                {quickView.is_call_for_pricing && quickView.call_for_pricing_phone && (
                  <a
                    href={telLink(quickView.call_for_pricing_phone)}
                    className="text-sm font-semibold text-amber-800 underline -mt-3 mb-4 inline-block"
                  >
                    {quickView.call_for_pricing_phone}
                  </a>
                )}
                <p className="text-sm text-gray-600 line-clamp-6 mb-4">{quickView.description}</p>
                <Link
                  to={tenantPath(`/product/${quickView.product_id}`)}
                  onClick={() => setQuickView(null)}
                  className="mt-auto block text-center bg-[#334155] text-white font-bold uppercase tracking-wider text-sm py-3 rounded-lg hover:bg-[#475569] transition-colors"
                >
                  View & Customize
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
